import { useState, useEffect, useCallback } from "react";
import { apiGet, ApiResponse, ApiError } from "../../services/api.service";
import type { Extra } from "../../types";

interface UseGetExtraReturn {
	extra: Extra | null;
	loading: boolean;
	error: string | null;
	refetch: () => Promise<void>;
}

/**
 * Custom hook to fetch a single extra/profile by ID
 * @param extraId - The ID of the extra to fetch (defaults to 4)
 * @returns Extra data, loading state, error state, and refetch function
 */
export function useGetExtra(extraId?: number | string): UseGetExtraReturn {
	const [extra, setExtra] = useState<Extra | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);

	const fetchExtra = useCallback(async () => {
		// Skip fetch if no extraId is provided
		if (!extraId) {
			setLoading(false);
			return;
		}

		try {
			setLoading(true);
			setError(null);

			const response: ApiResponse<Extra> = await apiGet<Extra>(
				`/extras/${extraId}`
			);

			if (response.success && response.data) {
				setExtra(response.data);
			} else {
				setError(response.error || "Fehler beim Abrufen des Extras");
				setExtra(null);
			}
		} catch (err) {
			if (err instanceof ApiError) {
				setError(err.message);
			} else if (err instanceof Error) {
				setError(err.message);
			} else {
				setError("Ein unerwarteter Fehler ist aufgetreten");
			}
			setExtra(null);
		} finally {
			setLoading(false);
		}
	}, [extraId]);

	useEffect(() => {
		if (extraId) {
			fetchExtra();
		}
	}, [fetchExtra, extraId]);

	return {
		extra,
		loading,
		error,
		refetch: fetchExtra,
	};
}
